import { useState } from "react";
import { LayoutDashboard, Stethoscope, ScrollText, PenLine, ChartColumnBig, UsersRound, ChevronLeft, ChevronRight, ChevronDown, GraduationCap, Users, MessageCircle, ChartPie, Building2, Archive, Trophy, MessagesSquare, NotebookPen, Bell, Sprout, CalendarCheck, Megaphone, Home, Monitor, Database, Clapperboard, BookOpen } from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import lurneaxLogo from "@/assets/lurneax-logo.svg";
import { useRole } from "@/contexts/RoleContext";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { usePathway } from "@/contexts/PathwayContext";
import { Button } from "@/components/ui/button";
import { useParentHasExamAssignments } from "@/hooks/use-parent-exam-assignments";

import { useParentSidebarVisibility } from "@/hooks/use-parent-sidebar-visibility";

import { useProjectName } from "@/hooks/use-project-name";
import { toast } from "sonner";
import { preloadPath } from "@/lib/routePreload";

const intentHandlers = (path: string) => {
  const warm = () => preloadPath(path);
  return { onPointerEnter: warm, onFocus: warm, onTouchStart: warm };
};

const PROSPECT_ALLOWED_URLS = new Set<string>([
  "/learning/courses",
  "/learning/dashboard",
  "/assessment/dashboard",
]);

interface AssessmentItem {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  allowedRoles: string[];
  requiresExamAssignment?: boolean;
  
  requiresDiagnosticResults?: boolean;
  requiresEndOfTermResults?: boolean;
  requiresExamResults?: boolean;
  showUnreadBadge?: boolean;
  showLiveBadge?: boolean;
  children?: AssessmentItem[];
}

const networkItems: AssessmentItem[] = [{
  title: "National Overview",
  url: "/national-dashboard",
  icon: Monitor,
  allowedRoles: ["master_admin", "admin", "state_partner", "growth_partner"]
}];

const assessmentItems: AssessmentItem[] = [{
  title: "Parent Dashboard",
  url: "/parent-dashboard",
  icon: Home,
  allowedRoles: ["parent"]
}, {
  title: "Quiz Dashboard",
  url: "/assessment/dashboard",
  icon: LayoutDashboard,
  allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"]
}, {
  title: "Diagnostic Tests",
  url: "/assessment/diagnostic-tests",
  icon: Stethoscope,
  allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"],
  children: [{
    title: "Diagnostic Test Results",
    url: "/assessment/results",
    icon: ChartColumnBig,
    allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"]
  }]
}, {
  title: "Diagnostic Test Results",
  url: "/assessment/results",
  icon: ChartColumnBig,
  allowedRoles: ["parent", "student"],
  requiresDiagnosticResults: true,
}, {
  title: "End of Term",
  url: "/assessment/end-of-term",
  icon: CalendarCheck,
  allowedRoles: ["master_admin", "admin", "center_manager", "state_partner", "growth_partner"],
  children: [{
    title: "End of Term Results",
    url: "/assessment/end-of-term-results",
    icon: ChartColumnBig,
    allowedRoles: ["master_admin", "admin", "center_manager", "state_partner", "growth_partner"]
  }, {
    title: "Manual Scoring",
    url: "/assessment/scoring?scope=eot",
    icon: PenLine,
    allowedRoles: ["master_admin", "admin", "center_manager", "tutor"]
  }]
}, {
  title: "End of Term Results",
  url: "/assessment/end-of-term-results",
  icon: ChartColumnBig,
  allowedRoles: ["parent", "tutor"],
  requiresEndOfTermResults: true,
}, {
  title: "Exams Management",
  url: "/assessment/exams",
  icon: ScrollText,
  allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"],
  children: [{
    title: "Exam Results",
    url: "/assessment/exam-results",
    icon: Trophy,
    allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"]
  }, {
    title: "Manual Scoring",
    url: "/assessment/scoring?scope=exam",
    icon: PenLine,
    allowedRoles: ["master_admin", "admin", "center_manager", "tutor"]
  }, {
    title: "OCPTC Library",
    url: "/ocptc/library",
    icon: BookOpen,
    allowedRoles: ["master_admin", "admin"]
  }]
}, {
  title: "Homework",
  url: "/assessment/homework-videos",
  icon: Clapperboard,
  allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"],
  children: [{
    title: "Homework Handouts",
    url: "/assessment/homework-handouts",
    icon: ScrollText,
    allowedRoles: ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"]
  }]
}, {
  title: "Resources",
  url: "/parent/homework",
  icon: Clapperboard,
  allowedRoles: ["parent"],
}, {
  title: "Downloadable Resources",
  url: "/resources/downloads",
  icon: BookOpen,
  allowedRoles: ["parent", "master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner"],
}, {
  title: "Homework Handouts",
  url: "/parent/homework?view=handouts",
  icon: ScrollText,
  allowedRoles: ["parent"],
}, {
  title: "Question Bank",
  url: "/assessment/question-bank",
  icon: Database,
  allowedRoles: ["master_admin", "admin"]
},
{
  title: "Exam Results",
  url: "/assessment/my-exam-results",
  icon: Trophy,
  allowedRoles: ["parent"],
  requiresExamResults: true,
},
{
  title: "My Students",
  url: "/assessment/students",
  icon: UsersRound,
  allowedRoles: ["master_admin", "admin", "tutor", "center_manager"],
}, {
  title: "Student Growth Areas",
  url: "/assessment/early-warning",
  icon: Sprout,
  allowedRoles: ["master_admin", "admin"]
}];
const adminItems = [{
  title: "Users",
  url: "/users",
  icon: Users,
  allowedRoles: ["master_admin", "admin", "center_manager"]
}, {
  title: "Centres",
  url: "/locations",
  icon: Building2,
  allowedRoles: ["master_admin", "admin"]
}, {
  title: "Communication",
  url: "/communication",
  icon: Megaphone,
  allowedRoles: ["master_admin", "admin"]
}, {
  title: "Reports",
  url: "/reports",
  icon: ChartPie,
  allowedRoles: ["master_admin", "admin", "center_manager"]
}];
export function AssessmentSidebar() {
  const {
    state,
    toggleSidebar
  } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const currentSearch = location.search;
  // Active-link matching that respects query params (e.g. ?scope=exam vs ?scope=eot
  // on /assessment/scoring). For plain paths we keep the prefix behaviour.
  const isActive = (url: string) => {
    const [urlPath, urlQuery] = url.split('?');
    if (urlQuery) {
      if (currentPath !== urlPath) return false;
      const current = new URLSearchParams(currentSearch);
      const target = new URLSearchParams('?' + urlQuery);
      for (const [k, v] of target.entries()) {
        if (current.get(k) !== v) return false;
      }
      return true;
    }
    // For a parent link that has children sharing the same base path,
    // only mark active on an exact pathname match so query-scoped children
    // don't bleed into the parent (and vice-versa).
    return currentPath === urlPath || currentPath.startsWith(urlPath + '/');
  };
  const {
    userRole
  } = useRole();
  const {
    setCurrentPathway
  } = usePathway();
  const { data: projectName } = useProjectName();

  // Check if parent has exam assignments
  const { data: hasExamAssignments } = useParentHasExamAssignments();
  
  
  
  
  // Check parent sidebar visibility (diagnostic, EOT, exam, assignments)
  const { data: parentVisibility } = useParentSidebarVisibility();

  // Only show pathway switcher for these roles
  const canSwitchPathway = userRole && ["master_admin", "admin", "center_manager", "tutor", "state_partner", "growth_partner", "franchisee_prospect"].includes(userRole);
  const isProspect = userRole === "franchisee_prospect";
  const handleProspectClick = (url: string) => (e: React.MouseEvent) => {
    if (isProspect && !PROSPECT_ALLOWED_URLS.has(url)) {
      e.preventDefault();
      toast.info("Contact your administrator for access to this section.");
    }
  };
  const handleLogoClick = () => {
    navigate('/assessment/dashboard');
  };
  const handleSwitchToLearning = () => {
    setCurrentPathway("learning");
    navigate('/learning/dashboard');
  };
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const toggleExpand = (title: string) => setExpandedItems(prev => ({ ...prev, [title]: !prev[title] }));
  const isItemOpen = (item: AssessmentItem) =>
    expandedItems[item.title] || (item.children?.some(c => isActive(c.url)) ?? false);

  const filteredNetworkItems = networkItems.filter(item => !!userRole && item.allowedRoles.includes(userRole));

  const filteredAssessmentItems = assessmentItems.filter(item => {
    // Check role permission
    if (!userRole || !item.allowedRoles.includes(userRole)) {
      return false;
    }
    // Check if exam assignment is required for parents
    if (item.requiresExamAssignment && userRole === 'parent' && !hasExamAssignments) {
      return false;
    }
    
    // Check parent-specific visibility flags
    if (userRole === 'parent' && parentVisibility) {
      if (item.requiresDiagnosticResults && !parentVisibility.hasDiagnosticResults) return false;
      if (item.requiresEndOfTermResults && !parentVisibility.hasEndOfTermResults) return false;
      if (item.requiresExamResults && !parentVisibility.hasExamResults) return false;
    }
    return true;
  }).map(item => ({
    ...item,
    children: item.children?.filter(child => !!userRole && child.allowedRoles.includes(userRole))
  }));
  
  return <TooltipProvider>
      <div className="relative group/sidebar">
        <Sidebar data-tour="sidebar" className={`${collapsed ? "w-14" : "w-64"} transition-all duration-300 ease-in-out`} collapsible="icon">
          <SidebarHeader className="p-3">
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                   <img loading="lazy" src={lurneaxLogo} alt="Lurneax" onClick={handleLogoClick} className="h-40 w-40 transition-all duration-300 hover:scale-105 cursor-pointer flex-shrink-0" />
                 </TooltipTrigger>
                 {collapsed && <TooltipContent side="right">Go to Dashboard</TooltipContent>}
               </Tooltip>
              {!collapsed && projectName && (
                <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase text-center">
                  {projectName}
                </p>
              )}
              
              {canSwitchPathway && <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size={collapsed ? "icon" : "sm"} onClick={handleSwitchToLearning} className="w-full gap-2 border-border/50 hover:border-primary/50 hover:bg-primary/5">
                      <GraduationCap className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      {!collapsed && <span className="text-xs">Switch to Learning</span>}
                    </Button>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">Switch to Learning</TooltipContent>}
                </Tooltip>}
            </div>
          </SidebarHeader>

          <SidebarContent>
            {filteredNetworkItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className={`transition-opacity duration-200 ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
                  Network
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredNetworkItems.map(item => (
                      <SidebarMenuItem key={item.title}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild isActive={isActive(item.url)}>
                              <Link to={item.url} onClick={handleProspectClick(item.url)} {...intentHandlers(item.url)} className="transition-all duration-200">
                                <item.icon className="h-4 w-4 flex-shrink-0" />
                                <span className={`transition-all duration-200 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                                  {item.title}
                                </span>
                              </Link>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          {collapsed && <TooltipContent side="right" className="font-medium bg-popover border-border shadow-lg">
                            <p>{item.title}</p>
                          </TooltipContent>}
                        </Tooltip>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SidebarGroupLabel className={`transition-opacity duration-200 ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
                Assessments
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredAssessmentItems.map(item => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild isActive={isActive(item.url)}>
                            <Link to={item.url} onClick={handleProspectClick(item.url)} {...intentHandlers(item.url)} className="transition-all duration-200 flex items-center justify-between w-full" data-tour={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                              <span className="flex items-center gap-2">
                                <item.icon className="h-4 w-4 flex-shrink-0" />
                                <span className={`transition-all duration-200 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                                  {item.title}
                                </span>
                              </span>
                              {item.children && item.children.length > 0 && !collapsed && (
                                <ChevronDown
                                  className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isItemOpen(item) ? 'rotate-180' : ''}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleExpand(item.title);
                                  }}
                                />
                              )}
                              {item.showLiveBadge && !collapsed && (
                                <Badge variant="secondary" className="h-5 flex items-center gap-1 px-1.5 text-xs border-green-200 bg-green-50 text-green-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                                  Live
                                </Badge>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {collapsed && <TooltipContent side="right" className="font-medium bg-popover border-border shadow-lg">
                            <p>{item.title}</p>
                          </TooltipContent>}
                      </Tooltip>
                      {/* Collapsible children */}
                      {item.children && item.children.length > 0 && !collapsed && isItemOpen(item) && (
                        <SidebarMenu className="ml-4 mt-1 border-l border-border/50 pl-2">
                          {item.children.map(child => (
                            <SidebarMenuItem key={child.title}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuButton asChild isActive={isActive(child.url)} className="text-sm">
                                    <Link to={child.url} onClick={handleProspectClick(child.url)} {...intentHandlers(child.url)} className="transition-all duration-200">
                                      <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span>{child.title}</span>
                                    </Link>
                                  </SidebarMenuButton>
                                </TooltipTrigger>
                              </Tooltip>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      )}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-border">
            <SidebarUserMenu adminItems={adminItems} />
          </SidebarFooter>
        </Sidebar>

        {/* Hover-activated collapse button on sidebar edge */}
        <button onClick={toggleSidebar} className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-3 z-50 h-6 w-6 rounded-full bg-background border border-border shadow-md items-center justify-center opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200 hover:bg-muted hover:scale-110">
          {collapsed ? <ChevronRight className="h-3 w-3 text-muted-foreground" /> : <ChevronLeft className="h-3 w-3 text-muted-foreground" />}
        </button>
      </div>
    </TooltipProvider>;
}